'use client';

import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/navigation';
import fetchContents from '@/redux/thunk/contents-thunk';
import { EXTERNAL, IMAGE, WEB, YOUTUBE_VIDEO } from '@/constants';
import { bannerTypes } from '@/constants/types';
import BannerImageComponent from '@/app/components/Image/BannerImageComponent';
import YoutubeIframe from '@/app/components/YoutubeIframe';
import BannerSkeleton from '@/app/components/Skeleton/BannerSkeleton';
import VideoPlayer from '@/app/components/VideoPlayer';

// swiper slider
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

function CampaignHeroBannerCarousel({ section }) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.sections);
  const { contents } = useSelector((state) => state.contents);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirst, setIsFirst] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [autoPlay, setAutoPlay] = useState({
    delay: Number(section?.details?.auto_play_delay),
    disableOnInteraction: false,
  });

  const hasAutoPlay = section?.details?.auto_play;

  useEffect(() => {
    setAutoPlay({
      delay: Number(section?.details?.auto_play_delay),
      disableOnInteraction: false,
    });
  }, [section]);

  // youtube embed video
  const onPlayerStateChange = (event) => {
    if (!swiperInstance) return;

    if (event.data === window.YT.PlayerState.PLAYING) {
      setIsPaused(true);
    } else if (
      event.data === window.YT.PlayerState.PAUSED ||
      event.data === window.YT.PlayerState.ENDED
    ) {
      setIsPaused(false);
    }
  };

  useEffect(() => {
    if (swiperInstance && swiperInstance.autoplay) {
      if (isPaused) {
        swiperInstance.autoplay.stop();
      } else if (hasAutoPlay) {
        swiperInstance.autoplay.start();
      }
    }
  }, [isPaused, swiperInstance]);

  useEffect(() => {
    if (swiperInstance && swiperInstance.autoplay) {
      swiperInstance.autoplay.stop();
      swiperInstance.params.autoplay.delay = autoPlay.delay; // Update delay
      swiperInstance.autoplay.start();
    }
  }, [autoPlay, swiperInstance]);

  useEffect(() => {
    dispatch(
      fetchContents({
        sectionId: section?.id,
        contentType: section?.content_type,
      }),
    );
  }, []);

  useEffect(() => {
    if (contents?.length > 0) {
      try {
        const parsedItems =
          typeof contents === 'string' ? JSON.parse(contents) : contents;
        const sortedItems = [...parsedItems].sort(
          (a, b) => a.priority - b.priority,
        );
        setData(sortedItems);
      } catch (error) {
        console.error('Error parsing contents:', error);
      }
    }
  }, [contents]);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(loadingTimeout);
  }, []);

  const handleUserInteraction = (interaction) => {
    setIsFirst(true);
    if (isFirst) {
      setIsPaused(interaction);
    }
  };

  const handleClickUrl = (url) => {
    if (!url) return null;

    if (url?.en?.type === WEB) {
      router.push(url?.en?.url);
    } else if (url?.en?.type === EXTERNAL) {
      window.open(url?.en?.url, '_blank');
    }
  };

  // youtube embed video
  const extractYouTubeId = (url) => {
    const match = url.match(
      /(?:youtube\.com\/(?:[^/]+\/[^/]+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/s]{11})/,
    );
    return match ? match[1] : null;
  };

  const renderContent = (item) => {
    if (item?.type === IMAGE) {
      return (
        <BannerImageComponent
          key={data[0]?.id}
          desktopSrc={item?.image?.large}
          mobileSrc={item?.image?.small}
          type={bannerTypes.default}
          alt={item?.title?.en || 'campaign hero banner image'}
        />
      );
    }
    if (item?.type === YOUTUBE_VIDEO) {
      const videoId = extractYouTubeId(item?.video);
      return (
        <div className="max-w-full w-full aspect-[1.04/1] md:aspect-[4.114/1] max-h-max">
          <YoutubeIframe
            playWithID
            videoId={videoId}
            onStateChange={onPlayerStateChange}
            className="w-full object-center object-contain max-w-full aspect-[1.04/1] md:aspect-[4.114/1] max-h-max"
          />
        </div>
      );
    }
    return (
      <VideoPlayer
        videoLink={item?.video}
        onUserInteraction={handleUserInteraction}
        className="max-w-full w-full aspect-[2/1] md:aspect-[4.114/1]"
        smallDeviceRatio="2 / 1"
        largeDeviceRatio="4.114 / 1"
        height="100%"
      />
    );
  };

  if (loading || isLoading) {
    return (
      <div className="w-full h-full">
        <BannerSkeleton
          className="aspect-[2/1] md:aspect-[4.114/1]"
          showNextPrevBtnSkeleton={false}
        />
      </div>
    );
  }

  return (
    <Swiper
      onSwiper={setSwiperInstance}
      modules={[Autoplay, Pagination]}
      slidesPerView={1}
      speed={800}
      autoplay={{
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      mousewheel="true"
      pagination={{ clickable: true }}
      className="campaign-hero-banner"
    >
      {data?.map((item) => (
        <SwiperSlide key={item.id}>
          <div
            className="aspect-[2/1] md:aspect-[4.11/1] relative overflow-hidden"
            role="button"
            tabIndex={0}
            onClick={() => handleClickUrl(item?.redirect_url)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClickUrl(item?.redirect_url?.en?.url);
              }
            }}
          >
            {renderContent(item)}
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

export default CampaignHeroBannerCarousel;
